import { useEffect } from "react"
import { useAppSelector } from "hooks"
import useSchedulesQueryParams from "./useSchedulesQueryParams"
import { isLocationLoading, filteredListBySearch } from "store/Location/locationDetails.slice"
import { selectBranchIsLoading, selectBranchList } from "store/Branches/branch.slice"
import { selectCustomerOptions } from "store/Customers/customers.slice"
import { isSDSuppliersLoading, selectSDSuppliersDropdownList } from "store/StaticData/SDSuppliers.slice"
import { filteredListBySearch as filteredEmployeeListSelector } from "store/Employee/detail.slice"
import { EmployeeTabs, LocationTabs } from "types/schedules.types"

/**
 * useScheduleDefaults
 *
 * Centralizes the "Initialization" and "Defaulting" logic for the Schedules pages.
 * It listens to the data sources (Redux) and the current URL params,
 * and applies default selections if params are missing but data is available.
 *
 * Usage: Call this ONCE in the top-level Page component (e.g., Location.tsx, Employee.tsx).
 */
export const useLocationScheduleDefaults = () => {
	const { selectedLocationBranch, selectedLocation, selectedCustomer, activeOnly, tab, setSearchParams } =
		useSchedulesQueryParams()

	// Data Selectors
	const branches = useAppSelector(selectBranchList())

	const filteredLocations = useAppSelector(
		filteredListBySearch(
			"",
			selectedLocationBranch || "",
			["shortName", "name"],
			selectedCustomer || "",
			activeOnly === "true",
		),
	)

	const customers = useAppSelector(selectCustomerOptions)

	const loadingLocation = useAppSelector(isLocationLoading())

	// --- Helpers (Pure in the sense that they return a value based on inputs) ---
	const getBranchDefault = (): Record<string, string> => {
		const hasBranches = branches.length > 0
		const isBranchValid = selectedLocationBranch && branches.some(branch => branch._id === selectedLocationBranch)

		if (hasBranches && !isBranchValid) {
			if (branches[0]?._id) {
				return { location_branch: branches[0]._id }
			}
		}

		return {}
	}

	const getCustomerDefault = (): Record<string, string> => {
		const hasCustomers = customers.length > 0
		const isCustomerValid =
			selectedCustomer === "all" || customers.some(customer => customer.value === selectedCustomer)

		if (!selectedCustomer || (hasCustomers && !isCustomerValid)) {
			return { customer: "all" }
		}

		return {}
	}

	const getLocationDefault = (): Record<string, string> => {
		if (selectedLocation === "new" && tab === LocationTabs.Detail) return {}

		const hasLocations = filteredLocations.length > 0
		const isLocationValid =
			selectedLocation && filteredLocations.some(location => location._id === selectedLocation)

		if (hasLocations && !isLocationValid) {
			if (filteredLocations[0]?._id) {
				return { location: filteredLocations[0]._id }
			}
		}

		return {}
	}

	// --- Defaulting Logic ---
	useEffect(() => {
		if (loadingLocation) return

		const updates: Record<string, string> = {
			...getBranchDefault(),
			...getCustomerDefault(),
			...getLocationDefault(),
		}

		if (Object.keys(updates).length > 0) {
			setSearchParams(updates)
		}
	}, [
		tab,
		branches,
		filteredLocations,
		customers,
		loadingLocation,
		selectedLocationBranch,
		selectedLocation,
		selectedCustomer,
		setSearchParams,
	])
}

export const useEmployeeScheduleDefaults = () => {
	const { selectedEmployeeBranch, selectedSupplier, selectedEmployee, setSearchParams, activeOnly, tab } =
		useSchedulesQueryParams()

	const branches = useAppSelector(selectBranchList())
	const suppliers = useAppSelector(selectSDSuppliersDropdownList)
	const branchesLoading = useAppSelector(selectBranchIsLoading())
	const suppliersLoading = useAppSelector(isSDSuppliersLoading())

	const filteredEmployees = useAppSelector(
		filteredEmployeeListSelector(
			"",
			["shortName", "firstName", "lastName"],
			activeOnly === "true",
			selectedEmployeeBranch,
			selectedSupplier,
		),
	)

	const getBranchOrSupplierDefault = (): Record<string, string> => {
		const hasBranches = branches.length > 0
		const hasSuppliers = suppliers.length > 1 // >1 instead of >0 because selectSDSuppliersDropdownList always includes a placeholder

		// 1. Default Branch vs Supplier (None selected)
		if (!selectedEmployeeBranch && !selectedSupplier) {
			if (hasBranches) {
				return { employee_branch: branches[0]._id }
			}
			if (hasSuppliers) {
				return { supplier: suppliers[1]?.value || suppliers[0]?.value }
			}
			return {}
		}

		// 2. Validation: if selected branch no longer exists
		const isBranchInvalid =
			selectedEmployeeBranch &&
			hasBranches &&
			!branches.some(branch => branch._id === selectedEmployeeBranch) &&
			!branchesLoading

		if (isBranchInvalid) {
			return { employee_branch: branches[0]._id }
		}

		// 3. Validation: if selected supplier no longer exists
		const isSupplierInvalid =
			selectedSupplier &&
			hasSuppliers &&
			!suppliers.some(supplier => supplier.value === selectedSupplier) &&
			!suppliersLoading

		if (isSupplierInvalid) {
			return { supplier: suppliers[1]?.value || suppliers[0]?.value }
		}

		return {}
	}

	const getEmployeeDefault = (): Record<string, string> => {
		// 4. Escape condition for 'new' employee creation view
		if (selectedEmployee === "new" && tab === EmployeeTabs.Detail) return {}

		const hasEmployees = filteredEmployees.length > 0
		const isEmployeeValid = selectedEmployee && filteredEmployees.some(emp => emp._id === selectedEmployee)

		// 5. Default to first employee if none selected or if explicitly selected one is invalid
		if (hasEmployees && !isEmployeeValid) {
			if (filteredEmployees[0]?._id) {
				return { employee: filteredEmployees[0]._id }
			}
		}

		return {}
	}

	useEffect(() => {
		const updates: Record<string, string> = {
			...getBranchOrSupplierDefault(),
			...getEmployeeDefault(),
		}

		if (Object.keys(updates).length > 0) {
			setSearchParams(updates)
		}
	}, [
		tab,
		branches,
		suppliers,
		filteredEmployees,
		selectedEmployeeBranch,
		selectedSupplier,
		selectedEmployee,
		setSearchParams,
	])
}
